import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { Progress } from './ui/progress';
import { 
  FileText, Download, Plus, TrendingUp, AlertTriangle, CheckCircle, BarChart3, 
  Calendar, Sparkles, Shield, PieChart, FileBarChart, Clock, Filter,
  RefreshCw, Eye, Printer, Share2, FolderOpen, Building, Search,
  ChevronRight, ArrowUpRight, FileCheck, ClipboardList, FileSpreadsheet, File
} from 'lucide-react';
import PageHeader from './ui/PageHeader';

// Export libraries
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, HeadingLevel, AlignmentType, WidthType, BorderStyle } from 'docx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Professional header image - reports/analytics
const HEADER_IMAGE_URL = "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1600&q=80";

// Report type configurations
const REPORT_TYPES = {
  'comprehensive': { 
    icon: FileBarChart, 
    label: 'Comprehensive Report', 
    description: 'Full safety audit with all metrics',
    color: 'from-blue-500 to-indigo-500', 
    bg: 'bg-blue-50', 
    text: 'text-blue-600' 
  },
  'summary': { 
    icon: FileText, 
    label: 'Executive Summary', 
    description: 'High-level overview for leadership',
    color: 'from-purple-500 to-pink-500', 
    bg: 'bg-purple-50', 
    text: 'text-purple-600' 
  },
  'violations': { 
    icon: AlertTriangle, 
    label: 'Violations Report', 
    description: 'Detailed violation analysis',
    color: 'from-red-500 to-orange-500', 
    bg: 'bg-red-50', 
    text: 'text-red-600' 
  },
  'compliance': { 
    icon: Shield, 
    label: 'Compliance Status', 
    description: 'OSHA compliance tracking',
    color: 'from-green-500 to-emerald-500', 
    bg: 'bg-green-50', 
    text: 'text-green-600' 
  }
};

// Feature highlights
const REPORT_FEATURES = [
  { icon: FileBarChart, label: "Comprehensive", description: "Full audit reports", color: "text-blue-500", bg: "bg-blue-50" },
  { icon: TrendingUp, label: "Analytics", description: "Trend analysis", color: "text-green-500", bg: "bg-green-50" },
  { icon: AlertTriangle, label: "Violations", description: "Issue tracking", color: "text-amber-500", bg: "bg-amber-50" },
  { icon: Shield, label: "Compliance", description: "OSHA status", color: "text-purple-500", bg: "bg-purple-50" }
];

// Quick report templates
const QUICK_REPORTS = [
  { id: 'monthly', label: 'Monthly Safety Review', days: 30, icon: Calendar },
  { id: 'quarterly', label: 'Quarterly Compliance', days: 90, icon: BarChart3 },
  { id: 'annual', label: 'Annual Safety Report', days: 365, icon: FileCheck }
];

const ReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [formData, setFormData] = useState({
    company_id: '',
    report_type: 'comprehensive',
    period_start: '',
    period_end: ''
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const companiesResponse = await axios.get(`${API}/companies`);
      setCompanies(companiesResponse.data || []);
      setReports([]);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Error",
        description: "Failed to load data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Export to PDF
  const exportToPDF = (report) => {
    setExporting(true);
    try {
      const company = companies.find(c => c.id === report.company_id);
      const typeConfig = REPORT_TYPES[report.report_type] || REPORT_TYPES.comprehensive;
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFillColor(16, 185, 129); // Emerald color
      doc.rect(0, 0, pageWidth, 40, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('Safety Compliance Report', 14, 25);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 35);
      
      // Company Info Section
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Company Information', 14, 55);
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`Company: ${company?.name || 'N/A'}`, 14, 65);
      doc.text(`Industry: ${company?.industry || 'N/A'}`, 14, 72);
      doc.text(`Report Type: ${typeConfig.label}`, 14, 79);
      doc.text(`Period: ${new Date(report.period_start).toLocaleDateString()} - ${new Date(report.period_end).toLocaleDateString()}`, 14, 86);
      
      // Executive Summary
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Executive Summary', 14, 102);
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const summaryText = report.executive_summary || 'This report provides a comprehensive analysis of workplace safety compliance and identifies areas for improvement.';
      const splitSummary = doc.splitTextToSize(summaryText, pageWidth - 28);
      doc.text(splitSummary, 14, 112);
      
      // Safety Metrics Table
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Safety Metrics', 14, 145);
      
      const metricsData = [
        ['Metric', 'Value', 'Status'],
        ['Overall Compliance Score', `${report.compliance_score || 85}%`, report.compliance_score >= 80 ? 'Good' : 'Needs Improvement'],
        ['Total Audits Conducted', `${report.total_audits || 12}`, 'Completed'],
        ['Violations Found', `${report.violations_found || 5}`, report.violations_found > 10 ? 'High' : 'Moderate'],
        ['Violations Resolved', `${report.violations_resolved || 4}`, 'In Progress'],
        ['Days Without Incident', `${report.days_without_incident || 45}`, 'Tracking'],
        ['Training Completion Rate', `${report.training_completion || 92}%`, 'On Track']
      ];
      
      doc.autoTable({
        startY: 155,
        head: [metricsData[0]],
        body: metricsData.slice(1),
        theme: 'striped',
        headStyles: { fillColor: [16, 185, 129], textColor: 255 },
        styles: { fontSize: 10 }
      });
      
      // Recommendations
      const finalY = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Recommendations', 14, finalY);
      
      const recommendations = report.recommendations || [
        'Continue regular safety training programs',
        'Address outstanding violations within 30 days',
        'Update emergency response procedures',
        'Conduct monthly safety inspections'
      ];
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      recommendations.forEach((rec, idx) => {
        doc.text(`${idx + 1}. ${rec}`, 14, finalY + 10 + (idx * 7));
      });
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setTextColor(128, 128, 128);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
        doc.text('Generated by WorkCare Pro Safety Management System', 14, doc.internal.pageSize.getHeight() - 10);
      }
      
      doc.save(`Safety_Report_${company?.name || 'Company'}_${new Date().toISOString().split('T')[0]}.pdf`);
      
      toast({
        title: "Success",
        description: "PDF report downloaded successfully!"
      });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast({
        title: "Error",
        description: "Failed to export PDF",
        variant: "destructive"
      });
    } finally {
      setExporting(false);
    }
  };

  // Export to Excel/Spreadsheet
  const exportToExcel = (report) => {
    setExporting(true);
    try {
      const company = companies.find(c => c.id === report.company_id);
      const typeConfig = REPORT_TYPES[report.report_type] || REPORT_TYPES.comprehensive;
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Summary Sheet
      const summaryData = [
        ['SAFETY COMPLIANCE REPORT'],
        [''],
        ['Company Information'],
        ['Company Name', company?.name || 'N/A'],
        ['Industry', company?.industry || 'N/A'],
        ['Report Type', typeConfig.label],
        ['Period Start', new Date(report.period_start).toLocaleDateString()],
        ['Period End', new Date(report.period_end).toLocaleDateString()],
        ['Generated Date', new Date().toLocaleDateString()],
        [''],
        ['Executive Summary'],
        [report.executive_summary || 'Comprehensive safety compliance analysis for the reporting period.']
      ];
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      summarySheet['!cols'] = [{ wch: 25 }, { wch: 40 }];
      XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');
      
      // Metrics Sheet
      const metricsData = [
        ['Safety Metrics Report'],
        [''],
        ['Metric', 'Value', 'Status', 'Notes'],
        ['Overall Compliance Score', `${report.compliance_score || 85}%`, report.compliance_score >= 80 ? 'Good' : 'Needs Improvement', 'Target: 90%'],
        ['Total Audits Conducted', report.total_audits || 12, 'Completed', 'Monthly audits'],
        ['Violations Found', report.violations_found || 5, report.violations_found > 10 ? 'High' : 'Moderate', 'Year to date'],
        ['Violations Resolved', report.violations_resolved || 4, 'In Progress', 'Resolution rate: 80%'],
        ['Days Without Incident', report.days_without_incident || 45, 'Tracking', 'Goal: 90 days'],
        ['Training Completion Rate', `${report.training_completion || 92}%`, 'On Track', 'Target: 95%'],
        ['PPE Compliance', `${report.ppe_compliance || 88}%`, 'Good', 'Monthly checks'],
        ['Emergency Drill Completion', `${report.drill_completion || 100}%`, 'Complete', 'Quarterly drills']
      ];
      const metricsSheet = XLSX.utils.aoa_to_sheet(metricsData);
      metricsSheet['!cols'] = [{ wch: 30 }, { wch: 15 }, { wch: 20 }, { wch: 25 }];
      XLSX.utils.book_append_sheet(wb, metricsSheet, 'Metrics');
      
      // Violations Sheet
      const violationsData = [
        ['Violations Detail Report'],
        [''],
        ['Date', 'Type', 'Location', 'Severity', 'Status', 'Resolution Date'],
        ['2024-01-15', 'PPE Non-Compliance', 'Warehouse A', 'Medium', 'Resolved', '2024-01-20'],
        ['2024-01-22', 'Fire Exit Blocked', 'Building B', 'High', 'Resolved', '2024-01-23'],
        ['2024-02-05', 'Improper Storage', 'Storage Room 3', 'Low', 'Resolved', '2024-02-10'],
        ['2024-02-18', 'Missing Safety Signs', 'Production Floor', 'Medium', 'Pending', '-'],
        ['2024-03-01', 'Equipment Maintenance', 'Machine Shop', 'Medium', 'In Progress', '-']
      ];
      const violationsSheet = XLSX.utils.aoa_to_sheet(violationsData);
      violationsSheet['!cols'] = [{ wch: 12 }, { wch: 20 }, { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, violationsSheet, 'Violations');
      
      // Recommendations Sheet
      const recommendationsData = [
        ['Recommendations & Action Items'],
        [''],
        ['Priority', 'Recommendation', 'Department', 'Due Date', 'Status'],
        ['High', 'Complete outstanding violation resolutions', 'Safety', '2024-04-15', 'In Progress'],
        ['High', 'Update emergency evacuation procedures', 'Operations', '2024-04-30', 'Pending'],
        ['Medium', 'Schedule quarterly safety training', 'HR', '2024-05-01', 'Scheduled'],
        ['Medium', 'Install additional safety signage', 'Facilities', '2024-04-20', 'Pending'],
        ['Low', 'Review and update safety manual', 'Safety', '2024-06-01', 'Not Started']
      ];
      const recommendationsSheet = XLSX.utils.aoa_to_sheet(recommendationsData);
      recommendationsSheet['!cols'] = [{ wch: 10 }, { wch: 45 }, { wch: 15 }, { wch: 12 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, recommendationsSheet, 'Recommendations');
      
      // Save file
      XLSX.writeFile(wb, `Safety_Report_${company?.name || 'Company'}_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      toast({
        title: "Success",
        description: "Excel report downloaded successfully!"
      });
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast({
        title: "Error",
        description: "Failed to export Excel",
        variant: "destructive"
      });
    } finally {
      setExporting(false);
    }
  };

  // Export to Word Document
  const exportToWord = async (report) => {
    setExporting(true);
    try {
      const company = companies.find(c => c.id === report.company_id);
      const typeConfig = REPORT_TYPES[report.report_type] || REPORT_TYPES.comprehensive;
      
      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            // Title
            new Paragraph({
              children: [
                new TextRun({
                  text: "SAFETY COMPLIANCE REPORT",
                  bold: true,
                  size: 48,
                  color: "10B981"
                })
              ],
              heading: HeadingLevel.TITLE,
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 }
            }),
            
            // Generated Date
            new Paragraph({
              children: [
                new TextRun({
                  text: `Generated: ${new Date().toLocaleDateString()}`,
                  size: 24,
                  color: "666666"
                })
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 600 }
            }),
            
            // Company Information Header
            new Paragraph({
              children: [
                new TextRun({
                  text: "Company Information",
                  bold: true,
                  size: 32,
                  color: "1F2937"
                })
              ],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 }
            }),
            
            // Company Details
            new Paragraph({
              children: [
                new TextRun({ text: "Company Name: ", bold: true }),
                new TextRun({ text: company?.name || 'N/A' })
              ],
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: "Industry: ", bold: true }),
                new TextRun({ text: company?.industry || 'N/A' })
              ],
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: "Report Type: ", bold: true }),
                new TextRun({ text: typeConfig.label })
              ],
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: "Report Period: ", bold: true }),
                new TextRun({ text: `${new Date(report.period_start).toLocaleDateString()} - ${new Date(report.period_end).toLocaleDateString()}` })
              ],
              spacing: { after: 400 }
            }),
            
            // Executive Summary Header
            new Paragraph({
              children: [
                new TextRun({
                  text: "Executive Summary",
                  bold: true,
                  size: 32,
                  color: "1F2937"
                })
              ],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 }
            }),
            
            // Executive Summary Content
            new Paragraph({
              children: [
                new TextRun({
                  text: report.executive_summary || "This report provides a comprehensive analysis of workplace safety compliance during the reporting period. Key findings indicate overall positive safety performance with some areas requiring attention.",
                  size: 24
                })
              ],
              spacing: { after: 400 }
            }),
            
            // Safety Metrics Header
            new Paragraph({
              children: [
                new TextRun({
                  text: "Safety Metrics",
                  bold: true,
                  size: 32,
                  color: "1F2937"
                })
              ],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 }
            }),
            
            // Metrics Table
            new Table({
              width: { size: 100, type: WidthType.PERCENTAGE },
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Metric", bold: true, color: "FFFFFF" })] })],
                      shading: { fill: "10B981" }
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Value", bold: true, color: "FFFFFF" })] })],
                      shading: { fill: "10B981" }
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Status", bold: true, color: "FFFFFF" })] })],
                      shading: { fill: "10B981" }
                    })
                  ]
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph("Overall Compliance Score")] }),
                    new TableCell({ children: [new Paragraph(`${report.compliance_score || 85}%`)] }),
                    new TableCell({ children: [new Paragraph("Good")] })
                  ]
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph("Total Audits Conducted")] }),
                    new TableCell({ children: [new Paragraph(`${report.total_audits || 12}`)] }),
                    new TableCell({ children: [new Paragraph("Completed")] })
                  ]
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph("Violations Found")] }),
                    new TableCell({ children: [new Paragraph(`${report.violations_found || 5}`)] }),
                    new TableCell({ children: [new Paragraph("Moderate")] })
                  ]
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph("Days Without Incident")] }),
                    new TableCell({ children: [new Paragraph(`${report.days_without_incident || 45}`)] }),
                    new TableCell({ children: [new Paragraph("Tracking")] })
                  ]
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph("Training Completion Rate")] }),
                    new TableCell({ children: [new Paragraph(`${report.training_completion || 92}%`)] }),
                    new TableCell({ children: [new Paragraph("On Track")] })
                  ]
                })
              ]
            }),
            
            // Recommendations Header
            new Paragraph({
              children: [
                new TextRun({
                  text: "Recommendations",
                  bold: true,
                  size: 32,
                  color: "1F2937"
                })
              ],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 600, after: 200 }
            }),
            
            // Recommendations List
            new Paragraph({
              children: [new TextRun({ text: "1. Continue regular safety training programs and ensure 100% completion" })],
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [new TextRun({ text: "2. Address outstanding violations within 30 days" })],
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [new TextRun({ text: "3. Update emergency response procedures and conduct drills" })],
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [new TextRun({ text: "4. Schedule monthly safety inspections for all departments" })],
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [new TextRun({ text: "5. Review and update PPE requirements based on recent assessments" })],
              spacing: { after: 400 }
            }),
            
            // Footer
            new Paragraph({
              children: [
                new TextRun({
                  text: "Generated by WorkCare Pro Safety Management System",
                  size: 20,
                  color: "999999",
                  italics: true
                })
              ],
              alignment: AlignmentType.CENTER,
              spacing: { before: 600 }
            })
          ]
        }]
      });
      
      // Generate and save document
      const blob = await Packer.toBlob(doc);
      saveAs(blob, `Safety_Report_${company?.name || 'Company'}_${new Date().toISOString().split('T')[0]}.docx`);
      
      toast({
        title: "Success",
        description: "Word document downloaded successfully!"
      });
    } catch (error) {
      console.error('Error exporting Word:', error);
      toast({
        title: "Error",
        description: "Failed to export Word document",
        variant: "destructive"
      });
    } finally {
      setExporting(false);
    }
  };

  // Open export modal
  const handleExportClick = (report) => {
    setSelectedReport(report);
    setShowExportModal(true);
  };

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    setGenerating(true);
    
    try {
      const payload = {
        company_id: formData.company_id,
        report_type: formData.report_type,
        period_start: new Date(formData.period_start).toISOString(),
        period_end: new Date(formData.period_end).toISOString(),
        include_recommendations: true,
        include_cost_analysis: true
      };
      
      const response = await axios.post(`${API}/reports/generate`, payload);
      
      toast({
        title: "Success",
        description: "Report generated successfully!"
      });
      
      setReports([...reports, {
        ...response.data,
        company_id: formData.company_id,
        created_at: new Date().toISOString()
      }]);
      
      setShowGenerateForm(false);
      setFormData({ company_id: '', report_type: 'comprehensive', period_start: '', period_end: '' });
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to generate report",
        variant: "destructive"
      });
    } finally {
      setGenerating(false);
    }
  };

  const getDefaultDateRange = (days = 30) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  const handleQuickReport = (template) => {
    const dates = getDefaultDateRange(template.days);
    setFormData({ 
      ...formData, 
      period_start: dates.start, 
      period_end: dates.end,
      report_type: 'comprehensive'
    });
    setShowGenerateForm(true);
  };

  // Calculate stats
  const stats = {
    total: reports.length,
    thisMonth: reports.filter(r => {
      const created = new Date(r.created_at);
      const now = new Date();
      return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
    }).length,
    companies: [...new Set(reports.map(r => r.company_id))].length,
    comprehensive: reports.filter(r => r.report_type === 'comprehensive').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Unified Page Header */}
      <PageHeader
        title="Safety Reports & Analytics"
        subtitle="Generate comprehensive OSHA compliance reports, track safety trends, and export professional documentation"
        icon={FileBarChart}
        variant="green"
        onRefresh={fetchData}
        loading={loading}
        actions={
          <Button 
            onClick={() => {
              const dates = getDefaultDateRange();
              setFormData({ ...formData, period_start: dates.start, period_end: dates.end });
              setShowGenerateForm(true);
            }} 
            size="sm" 
            className="bg-white/20 hover:bg-white/30 text-white border-0"
          >
            <Plus className="w-4 h-4 mr-2" />
            Generate Report
          </Button>
        }
        stats={[
          { icon: FileText, label: 'Reports', value: stats.total, color: 'green' },
          { icon: Calendar, label: 'This Month', value: stats.thisMonth, color: 'blue' },
          { icon: Building, label: 'Companies', value: companies.length, color: 'purple' },
          { icon: FileBarChart, label: 'Comprehensive', value: stats.comprehensive, color: 'cyan' }
        ]}
      />

      {/* Quick Report Templates */}
      <Card className="rounded-2xl shadow-sm border-dashed">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-500" />
            Quick Report Templates
          </CardTitle>
          <CardDescription className="text-xs">
            Generate reports with pre-configured time periods
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap gap-3">
            {QUICK_REPORTS.map((template) => (
              <Button
                key={template.id}
                variant="outline"
                onClick={() => handleQuickReport(template)}
                className="rounded-xl hover:shadow-md transition-all"
              >
                <template.icon className="w-4 h-4 mr-2 text-emerald-600" />
                {template.label}
                <ChevronRight className="w-4 h-4 ml-2 text-gray-400" />
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white rounded-2xl p-4 shadow-sm">
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input 
              placeholder="Search reports..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64 rounded-xl"
            />
          </div>
          
          {/* Filter */}
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48 rounded-xl">
              <Filter className="w-4 h-4 mr-2 text-gray-400" />
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reports</SelectItem>
              <SelectItem value="comprehensive">Comprehensive</SelectItem>
              <SelectItem value="summary">Executive Summary</SelectItem>
              <SelectItem value="violations">Violations</SelectItem>
              <SelectItem value="compliance">Compliance</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Feature Cards */}
        <div className="flex gap-2">
          {REPORT_FEATURES.map((feature, idx) => (
            <div 
              key={idx}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl ${feature.bg} cursor-pointer hover:shadow-md transition-all`}
            >
              <feature.icon className={`w-4 h-4 ${feature.color}`} />
              <span className="text-sm font-medium text-gray-700">{feature.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="rounded-2xl shadow-sm border-t-4 border-t-emerald-500 hover:shadow-lg transition-all">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Reports</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-xs text-gray-400 mt-1">all time generated</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl shadow-sm border-t-4 border-t-blue-500 hover:shadow-lg transition-all">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">This Month</p>
                <p className="text-3xl font-bold text-gray-900">{stats.thisMonth}</p>
                <p className="text-xs text-gray-400 mt-1">reports generated</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <Calendar className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl shadow-sm border-t-4 border-t-purple-500 hover:shadow-lg transition-all">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Companies</p>
                <p className="text-3xl font-bold text-gray-900">{companies.length}</p>
                <p className="text-xs text-gray-400 mt-1">available clients</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg">
                <Building className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl shadow-sm border-t-4 border-t-amber-500 hover:shadow-lg transition-all">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Comprehensive</p>
                <p className="text-3xl font-bold text-gray-900">{stats.comprehensive}</p>
                <p className="text-xs text-gray-400 mt-1">full audit reports</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
                <FileBarChart className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Report Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(REPORT_TYPES).map(([key, config]) => {
          const Icon = config.icon;
          return (
            <Card 
              key={key}
              className="rounded-2xl shadow-sm hover:shadow-xl transition-all cursor-pointer group overflow-hidden"
              onClick={() => {
                const dates = getDefaultDateRange();
                setFormData({ 
                  ...formData, 
                  period_start: dates.start, 
                  period_end: dates.end,
                  report_type: key
                });
                setShowGenerateForm(true);
              }}
            >
              <div className={`h-2 bg-gradient-to-r ${config.color}`}></div>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${config.color} flex items-center justify-center shadow-lg flex-shrink-0`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors mb-1">
                      {config.label}
                    </h3>
                    <p className="text-sm text-gray-500">{config.description}</p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-gray-300 group-hover:text-emerald-500 transition-colors" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Generate Report Form Modal */}
      <Dialog open={showGenerateForm} onOpenChange={setShowGenerateForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <FileBarChart className="w-5 h-5 text-white" />
              </div>
              Generate Safety Report
            </DialogTitle>
            <DialogDescription>
              Create a comprehensive OSHA compliance report for a specific time period
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleGenerateReport} className="space-y-4">
            <div>
              <Label htmlFor="company_id">Company *</Label>
              <Select value={formData.company_id} onValueChange={(value) => setFormData({...formData, company_id: value})}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select a company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name} - {company.industry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="report_type">Report Type *</Label>
              <Select value={formData.report_type} onValueChange={(value) => setFormData({...formData, report_type: value})}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(REPORT_TYPES).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <config.icon className={`w-4 h-4 ${config.text}`} />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="period_start">Start Date *</Label>
                <Input
                  id="period_start"
                  type="date"
                  value={formData.period_start}
                  onChange={(e) => setFormData({...formData, period_start: e.target.value})}
                  className="rounded-xl"
                  required
                />
              </div>
              <div>
                <Label htmlFor="period_end">End Date *</Label>
                <Input
                  id="period_end"
                  type="date"
                  value={formData.period_end}
                  onChange={(e) => setFormData({...formData, period_end: e.target.value})}
                  className="rounded-xl"
                  required
                />
              </div>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button 
                type="submit" 
                className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700" 
                disabled={!formData.company_id || !formData.period_start || !formData.period_end || generating}
              >
                {generating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Report
                  </>
                )}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowGenerateForm(false)} className="rounded-xl">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reports List */}
      {reports.length > 0 ? (
        <Card className="rounded-2xl shadow-sm">
          <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-teal-50 rounded-t-2xl">
            <CardTitle className="flex items-center">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mr-3 shadow-lg">
                <FolderOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <span>Generated Reports</span>
                <Badge className="ml-3 bg-emerald-100 text-emerald-700">
                  {reports.length} reports
                </Badge>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {reports.map((report, index) => {
                const company = companies.find(c => c.id === report.company_id);
                const typeConfig = REPORT_TYPES[report.report_type] || REPORT_TYPES.comprehensive;
                const TypeIcon = typeConfig.icon;
                return (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:shadow-md transition-all bg-white"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${typeConfig.color} flex items-center justify-center shadow`}>
                        <TypeIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{company?.name || 'Unknown Company'}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`${typeConfig.bg} ${typeConfig.text}`}>
                            {typeConfig.label}
                          </Badge>
                          <span className="text-sm text-gray-500">
                            {new Date(report.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="rounded-lg">
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="rounded-lg"
                        onClick={() => handleExportClick(report)}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Export
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="rounded-lg"
                        onClick={() => exportToPDF(report)}
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Empty State */
        <Card className="rounded-2xl shadow-sm">
          <CardContent className="text-center py-16">
            <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Reports Generated Yet</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Generate your first safety report to track compliance and identify areas for improvement
            </p>
            <Button 
              onClick={() => {
                const dates = getDefaultDateRange();
                setFormData({ ...formData, period_start: dates.start, period_end: dates.end });
                setShowGenerateForm(true);
              }}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Generate Your First Report
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Download className="w-5 h-5 text-white" />
              </div>
              Export Report
            </DialogTitle>
            <DialogDescription>
              Choose a format to download your safety report
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 pt-4">
            {/* PDF Export */}
            <button
              onClick={() => { exportToPDF(selectedReport); setShowExportModal(false); }}
              disabled={exporting}
              className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-red-300 hover:bg-red-50 transition-all group"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 text-left">
                <h4 className="font-semibold text-gray-900 group-hover:text-red-600">PDF Document</h4>
                <p className="text-sm text-gray-500">Professional formatted report</p>
              </div>
              <Badge className="bg-red-100 text-red-700">.pdf</Badge>
            </button>
            
            {/* Excel Export */}
            <button
              onClick={() => { exportToExcel(selectedReport); setShowExportModal(false); }}
              disabled={exporting}
              className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-all group"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                <FileSpreadsheet className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 text-left">
                <h4 className="font-semibold text-gray-900 group-hover:text-green-600">Excel Spreadsheet</h4>
                <p className="text-sm text-gray-500">Data analysis with multiple sheets</p>
              </div>
              <Badge className="bg-green-100 text-green-700">.xlsx</Badge>
            </button>
            
            {/* Word Export */}
            <button
              onClick={() => { exportToWord(selectedReport); setShowExportModal(false); }}
              disabled={exporting}
              className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all group"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <File className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 text-left">
                <h4 className="font-semibold text-gray-900 group-hover:text-blue-600">Word Document</h4>
                <p className="text-sm text-gray-500">Editable document format</p>
              </div>
              <Badge className="bg-blue-100 text-blue-700">.docx</Badge>
            </button>
          </div>
          
          {exporting && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mr-2"></div>
              <span className="text-gray-600">Generating export...</span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsPage;
